// One source of truth for Mail's UI preferences (Doc/MAIL_UI_REDESIGN.md —
// "remembered locally AND saved in the database").
//
//  • Boot: index.html's inline script applies theme / palette / top bar / density
//    from the local mirror before first paint; this module re-applies the same on
//    import and keeps <html> in step afterwards.
//  • Local mirror: localStorage `fg:mail:prefs:v1:<email>` = { prefs, base,
//    version, pending, migration }, plus `fg:mail:prefs:v1:last` = <email> so
//    the boot script (which runs before sign-in) knows whose mirror to read.
//  • Server: GET/PUT /api/mail/preferences (Mail BFF, PreferenceEndpoints.cs). Changes
//    apply instantly, are written to the mirror and PUT after ~500 ms as a patch with
//    If-Match: <version>. A 409 carries the server's current document: adopt it and
//    re-apply the local changes on top. Network errors keep the mirror and retry.
//  • First run after the redesign deploy: the old browser keys (general prefs, theme,
//    conversation view, shortcuts, list width, notifications) are folded in once and
//    uploaded only if the server has no row for the user.
//
// The field list and validation mirror bff/Preferences/MailPreferencesSchema.cs.

//  • Theme, accent and top bar are SUITE settings (@fg/ui-theme appearance.ts —
//    one look for every TribeX app unless Mail has its own). `appearance.theme /
//    palette / appbar` here mirror that store, and updatePreferences routes changes to
//    it; Mail's own copy in the DB only seeds the suite look the first time.

import { useEffect, useRef, useSyncExternalStore } from "react";
import { useAuth } from "react-oidc-context";
import {
    effectiveLook,
    initSuiteAppearance,
    resolveMode,
    setLook as setSuiteLook,
    subscribeAppearance,
    useAppearanceSync,
    type Look
} from "@fg/ui-theme";
import { mailApi, type PreferencesEnvelope } from "@/api/mailApi";
import { PALETTES, type PaletteId } from "./palettes";

export type ThemePref = "light" | "dark" | "system";
export type AppbarStyle = "accent" | "light" | "dark";
export type Density = "roomy" | "cozy" | "compact";
export type ReadingPanePref = "right" | "bottom" | "off";
export type HoverAction = "archive" | "delete" | "read" | "flag" | "pin" | "snooze" | "move" | "task";
export type { PaletteId };

export const THEMES: readonly ThemePref[] = ["light", "dark", "system"];
export const APPBAR_STYLES: readonly AppbarStyle[] = ["accent", "light", "dark"];
export const DENSITIES: readonly Density[] = ["roomy", "cozy", "compact"];
export const READING_PANES: readonly ReadingPanePref[] = ["right", "bottom", "off"];
export const HOVER_ACTIONS: readonly HoverAction[] = ["archive", "delete", "read", "flag", "pin", "snooze", "move", "task"];
export const LIST_WIDTH = { min: 280, max: 760, default: 420 } as const;

export interface MailPreferences {
    appearance: { theme: ThemePref; palette: PaletteId; appbar: AppbarStyle; density: Density };
    layout: {
        readingPane: ReadingPanePref;
        conversationView: boolean;
        /** Message-list column width (px) with the reading pane on the right. */
        listWidth: number;
        folderPane: boolean;
        myDay: boolean;
    };
    /** inboxTabs: the inbox's Direct / Updates tabs (replaces the never-shipped `inboxSplit`, which every saved row holds as false). */
    list: { hoverActions: HoverAction[]; inboxTabs: boolean };
    general: { markReadDelay: number; showPreview: boolean; keyboardShortcuts: boolean };
    notifications: { newMail: boolean; sound: boolean; showSender: boolean };
}

type Section = keyof MailPreferences;
export type PreferencesPatch = { [S in Section]?: Partial<MailPreferences[S]> };

export function defaultPreferences(): MailPreferences {
    return {
        appearance: { theme: "system", palette: "office", appbar: "accent", density: "cozy" },
        layout: { readingPane: "right", conversationView: true, listWidth: LIST_WIDTH.default, folderPane: true, myDay: false },
        list: { hoverActions: ["archive", "delete", "read", "flag"], inboxTabs: true },
        general: { markReadDelay: 2, showPreview: true, keyboardShortcuts: true },
        notifications: { newMail: true, sound: false, showSender: true }
    };
}

// ── Validation (mirror of MailPreferencesSchema.cs) ─────────────────────────

type Check = (v: unknown) => unknown | undefined; // → cleaned value, or undefined when invalid

const oneOf = (list: readonly string[]): Check => (v) => (typeof v === "string" && list.includes(v) ? v : undefined);
const bool: Check = (v) => (typeof v === "boolean" ? v : undefined);
const int = (min: number, max: number): Check => (v) =>
    typeof v === "number" && Number.isInteger(v) && v >= min && v <= max ? v : undefined;
const actions: Check = (v) => {
    if (!Array.isArray(v) || v.length > HOVER_ACTIONS.length) return undefined;
    if (!v.every((x) => typeof x === "string" && (HOVER_ACTIONS as readonly string[]).includes(x))) return undefined;
    return [...new Set(v as string[])];
};

const SCHEMA: { [S in Section]: { [K in keyof MailPreferences[S]]: Check } } = {
    appearance: {
        theme: oneOf(THEMES),
        palette: oneOf(PALETTES.map((p) => p.id)),
        appbar: oneOf(APPBAR_STYLES),
        density: oneOf(DENSITIES)
    },
    layout: {
        readingPane: oneOf(READING_PANES),
        conversationView: bool,
        listWidth: int(LIST_WIDTH.min, LIST_WIDTH.max),
        folderPane: bool,
        myDay: bool
    },
    list: { hoverActions: actions, inboxTabs: bool },
    general: { markReadDelay: int(0, 10), showPreview: bool, keyboardShortcuts: bool },
    notifications: { newMail: bool, sound: bool, showSender: bool }
};

const SECTIONS = Object.keys(SCHEMA) as Section[];

/** Keeps only known sections/keys with valid values. */
export function sanitizePatch(raw: unknown): PreferencesPatch {
    const out: Record<string, Record<string, unknown>> = {};
    if (!raw || typeof raw !== "object") return out;
    for (const section of SECTIONS) {
        const src = (raw as Record<string, unknown>)[section];
        if (!src || typeof src !== "object") continue;
        const checks = SCHEMA[section] as Record<string, Check>;
        for (const [key, check] of Object.entries(checks)) {
            const value = check((src as Record<string, unknown>)[key]);
            if (value !== undefined) (out[section] ??= {})[key] = value;
        }
    }
    return out as PreferencesPatch;
}

function isEmpty(patch: PreferencesPatch | null | undefined): boolean {
    return !patch || SECTIONS.every((s) => !patch[s] || Object.keys(patch[s] as object).length === 0);
}

/** `patch` over `base`; sections the patch doesn't touch keep their identity (cheap selector re-renders). */
function applyPatch(base: MailPreferences, patch: PreferencesPatch | null): MailPreferences {
    if (isEmpty(patch)) return base;
    const next = { ...base } as Record<Section, unknown>;
    for (const section of SECTIONS) {
        const part = patch?.[section];
        if (!part) continue;
        const current = base[section] as Record<string, unknown>;
        const changed = Object.entries(part).some(([k, v]) => JSON.stringify(current[k]) !== JSON.stringify(v));
        if (changed) next[section] = { ...current, ...part };
    }
    return next as unknown as MailPreferences;
}

function mergePatches(...patches: Array<PreferencesPatch | null | undefined>): PreferencesPatch | null {
    const out: Record<string, Record<string, unknown>> = {};
    for (const p of patches) {
        if (!p) continue;
        for (const section of SECTIONS) {
            const part = p[section];
            if (part) out[section] = { ...(out[section] ?? {}), ...part };
        }
    }
    return isEmpty(out as PreferencesPatch) ? null : (out as PreferencesPatch);
}

export function normalizePreferences(raw: unknown): MailPreferences {
    return applyPatch(defaultPreferences(), sanitizePatch(raw));
}

// ── Local mirror ─────────────────────────────────────────────────────────

const MIRROR_PREFIX = "fg:mail:prefs:v1:";
const LAST_USER_KEY = `${MIRROR_PREFIX}last`;
const mirrorKey = (user: string) => `${MIRROR_PREFIX}${user}`;

interface Mirror {
    /** Effective preferences — what the boot script in index.html reads. */
    prefs: MailPreferences;
    /** Last server-confirmed document and its version (0 = no row known). */
    base: MailPreferences;
    version: number;
    /** Local changes not yet confirmed by the server. */
    pending: PreferencesPatch | null;
    /** Old browser keys, uploaded only if the server turns out to have no row. */
    migration: PreferencesPatch | null;
}

function storageGet(key: string): string | null {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function storageSet(key: string, value: string) {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // storage blocked / full — the server copy still has it
    }
}

function readJson(key: string): unknown {
    const raw = storageGet(key);
    if (raw === null) return undefined;
    try {
        return JSON.parse(raw) as unknown;
    } catch {
        return undefined;
    }
}

function readMirror(user: string): Mirror | null {
    const raw = readJson(mirrorKey(user));
    if (!raw || typeof raw !== "object") return null;
    const m = raw as Record<string, unknown>;
    const base = normalizePreferences(m.base ?? m.prefs);
    const version = typeof m.version === "number" && Number.isInteger(m.version) && m.version >= 0 ? m.version : 0;
    const pending = isEmpty(sanitizePatch(m.pending)) ? null : sanitizePatch(m.pending);
    const migration = isEmpty(sanitizePatch(m.migration)) ? null : sanitizePatch(m.migration);
    return { base, version, pending, migration, prefs: applyPatch(base, mergePatches(migration, pending)) };
}

/**
 * The pre-redesign browser keys, folded into the new model once per user.
 * Returns null when none of them exist (a brand-new browser).
 */
function readLegacy(user: string): PreferencesPatch | null {
    const raw: Record<string, Record<string, unknown>> = {};
    const put = (section: Section, key: string, value: unknown) => {
        if (value !== undefined) (raw[section] ??= {})[key] = value;
    };

    const general = readJson("fg:mail:general-prefs");
    if (general && typeof general === "object") {
        const g = general as Record<string, unknown>;
        put("layout", "readingPane", g.readingPane === "hidden" ? "off" : g.readingPane);
        put("appearance", "density", g.density === "comfortable" ? "cozy" : g.density);
        put("general", "showPreview", g.showPreview);
        put("general", "markReadDelay", g.markReadDelay);
    }
    const theme = storageGet("fg-mail-theme");
    if (theme === "light" || theme === "dark") put("appearance", "theme", theme);
    const who = (user || "anon").toLowerCase();
    put("layout", "conversationView", readJson(`fg:mail:conversation-view:${who}`));
    put("general", "keyboardShortcuts", readJson(`fg:mail:keyboard-shortcuts:${who}`));
    const width = storageGet("fg.mail.listWidth");
    if (width !== null) put("layout", "listWidth", Math.round(Number(width)));
    const notif = readJson("fg:mail:notif-prefs");
    if (notif && typeof notif === "object") {
        const n = notif as Record<string, unknown>;
        put("notifications", "newMail", n.newMail);
        put("notifications", "sound", n.sound);
        put("notifications", "showSender", n.showSender);
    }
    const patch = sanitizePatch(raw);
    return isEmpty(patch) ? null : patch;
}

// ── Store ────────────────────────────────────────────────────────────────

export type SyncStatus = "local" | "saving" | "synced" | "offline";

interface Snapshot {
    user: string | null;
    prefs: MailPreferences;
    /** The theme actually shown ("system" resolved against the OS). */
    resolvedTheme: "light" | "dark";
    sync: SyncStatus;
}

let base = defaultPreferences();
let version = 0;
let pending: PreferencesPatch | null = null;
let inflight: PreferencesPatch | null = null;
let migration: PreferencesPatch | null = null;
let serverKnown = false;
let snapshot: Snapshot = { user: null, prefs: base, resolvedTheme: "light", sync: "local" };
const listeners = new Set<() => void>();

/** Density onto <html> (the suite store applies theme, palette and top bar) — as the boot script does. */
function applyToDocument(prefs: MailPreferences) {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-density", prefs.appearance.density);
}

/** Theme, palette and top bar as the suite appearance has them for Mail. */
function withSuiteLook(prefs: MailPreferences): MailPreferences {
    const look = effectiveLook("mail");
    const a = prefs.appearance;
    if (a.theme === look.mode && a.palette === look.palette && a.appbar === look.appbar) return prefs;
    return { ...prefs, appearance: { ...a, theme: look.mode, palette: look.palette, appbar: look.appbar } };
}

/**
 * The look Mail saved before appearance became suite-wide — seeds the suite look
 * once, when neither the account nor this browser has one yet.
 */
export function mailSavedLook(): Partial<Look> | null {
    const a = applyPatch(base, mergePatches(migration, inflight, pending)).appearance;
    const d = defaultPreferences().appearance;
    if (a.theme === d.theme && a.palette === d.palette && a.appbar === d.appbar) return null;
    return { mode: a.theme, palette: a.palette, appbar: a.appbar };
}

function emit(next: Partial<Snapshot>) {
    const merged = { ...snapshot, ...next };
    if (
        merged.user === snapshot.user && merged.prefs === snapshot.prefs &&
        merged.resolvedTheme === snapshot.resolvedTheme && merged.sync === snapshot.sync
    ) return;
    snapshot = merged;
    for (const l of listeners) l();
}

/** Recomputes the effective preferences from base + unconfirmed changes and shows them. */
function recompute() {
    const prefs = withSuiteLook(applyPatch(base, mergePatches(migration, inflight, pending)));
    applyToDocument(prefs);
    emit({ prefs, resolvedTheme: resolveMode(prefs.appearance.theme) });
}

function persist() {
    const user = snapshot.user;
    if (!user) return;
    const mirror: Mirror = {
        prefs: snapshot.prefs,
        base,
        version,
        pending: mergePatches(inflight, pending),
        migration
    };
    storageSet(mirrorKey(user), JSON.stringify(mirror));
    storageSet(LAST_USER_KEY, user);
}

function loadUser(user: string | null) {
    const mirror = user ? readMirror(user) : null;
    if (mirror) {
        ({ base, version, pending, migration } = mirror);
    } else {
        base = defaultPreferences();
        version = 0;
        pending = null;
        // No mirror yet: the old keys are what this browser showed until now.
        migration = readLegacy(user ?? "");
    }
    inflight = null;
    serverKnown = false;
    emit({ user });
    recompute();
}

// Boot, at module evaluation: the last signed-in user's mirror (index.html has already
// painted from it; this keeps React's first render consistent with that paint).
if (typeof window !== "undefined") {
    initSuiteAppearance("mail");
    const last = storageGet(LAST_USER_KEY);
    loadUser(last && last.includes("@") ? last : null);
    // A new look — chosen here, in another app, or the OS switching light/dark.
    subscribeAppearance(recompute);
}

export function readPreferences(): MailPreferences {
    return snapshot.prefs;
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** Current preferences, or a slice of them (re-renders only when the slice changes). */
export function usePreferences(): MailPreferences;
export function usePreferences<T>(select: (prefs: MailPreferences) => T): T;
export function usePreferences<T>(select?: (prefs: MailPreferences) => T): T | MailPreferences {
    return useSyncExternalStore(subscribe, () => (select ? select(snapshot.prefs) : snapshot.prefs));
}

export function useResolvedTheme(): "light" | "dark" {
    return useSyncExternalStore(subscribe, () => snapshot.resolvedTheme);
}

export function usePreferencesSyncStatus(): SyncStatus {
    return useSyncExternalStore(subscribe, () => snapshot.sync);
}

const SAVE_DEBOUNCE_MS = 500;

/** Apply a change now (UI + local mirror) and save it to the account shortly after. */
export function updatePreferences(patch: PreferencesPatch) {
    // Theme, accent and top bar belong to the suite appearance (every app, or Mail's own look).
    const { theme, palette, appbar, ...mailOnly } = patch.appearance ?? {};
    if (theme !== undefined || palette !== undefined || appbar !== undefined) {
        const look: Partial<Look> = {};
        if (theme) look.mode = theme;
        if (palette) look.palette = palette;
        if (appbar) look.appbar = appbar;
        setSuiteLook("mail", look);
    }
    const clean = sanitizePatch({ ...patch, appearance: mailOnly });
    if (isEmpty(clean)) return;
    pending = mergePatches(pending, clean);
    recompute();
    persist();
    if (snapshot.user) {
        emit({ sync: "saving" });
        scheduleFlush(SAVE_DEBOUNCE_MS);
    }
}

/** Back to the defaults for the given sections (all but notifications by default). */
export function resetPreferences(sections: Section[] = ["appearance", "layout", "list", "general"]) {
    const d = defaultPreferences();
    const patch: PreferencesPatch = {};
    for (const s of sections) (patch as Record<string, unknown>)[s] = d[s];
    // Resetting Mail must not change how every other app looks: keep the suite look.
    if (patch.appearance) patch.appearance = { density: d.appearance.density };
    updatePreferences(patch);
}

// ── Server sync ──────────────────────────────────────────────────────────

let getToken: () => string | undefined = () => undefined;
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let retryDelay = 0;
let conflictStreak = 0;
let pulledAt = 0;
let pulling = false;

function scheduleFlush(ms: number) {
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(() => {
        flushTimer = undefined;
        void flush();
    }, ms);
}

function scheduleRetry() {
    retryDelay = Math.min(Math.max(2_000, retryDelay * 2), 120_000);
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = setTimeout(() => {
        retryTimer = undefined;
        void (serverKnown ? flush() : pull());
    }, retryDelay);
    emit({ sync: "offline" });
}

function adopt(env: PreferencesEnvelope) {
    base = normalizePreferences(env.prefs);
    version = Number.isInteger(env.version) && env.version >= 0 ? env.version : 0;
}

async function pull() {
    const user = snapshot.user;
    const token = getToken();
    if (!user || !token || pulling) return;
    pulling = true;
    try {
        const env = await mailApi.getPreferences(token);
        if (snapshot.user !== user) return;
        pulledAt = Date.now();
        serverKnown = true;
        retryDelay = 0;
        adopt(env);
        if (migration) {
            // Upload the old browser values only when the account has nothing saved yet.
            if (version === 0) pending = mergePatches(migration, pending);
            migration = null;
        }
        recompute();
        persist();
        if (pending) void flush();
        else emit({ sync: "synced" });
    } catch {
        if (snapshot.user === user) scheduleRetry();
    } finally {
        pulling = false;
    }
}

async function flush() {
    const user = snapshot.user;
    const token = getToken();
    if (!user || inflight || !pending) return;
    if (!token) {
        emit({ sync: "local" });
        return;
    }
    if (!serverKnown) {
        void pull(); // learns the version (and resolves any migration), then flushes
        return;
    }
    if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = undefined;
    }
    const sent: PreferencesPatch = pending;
    inflight = sent;
    pending = null;
    emit({ sync: "saving" });

    let res: { status: number; body: PreferencesEnvelope | null };
    try {
        res = await mailApi.putPreferences(sent, version, token);
    } catch {
        inflight = null;
        pending = mergePatches(sent, pending);
        if (snapshot.user === user) {
            persist();
            scheduleRetry();
        }
        return;
    }
    inflight = null;
    if (snapshot.user !== user) return; // signed out / switched meanwhile; the old user's mirror still holds it

    if (res.status === 200 && res.body) {
        adopt(res.body);
        conflictStreak = 0;
        retryDelay = 0;
        recompute();
        persist();
        if (pending) scheduleFlush(SAVE_DEBOUNCE_MS);
        else emit({ sync: "synced" });
        return;
    }
    if (res.status === 409 && res.body) {
        // Another device saved first: take its document, then re-apply ours on top.
        adopt(res.body);
        pending = mergePatches(sent, pending);
        recompute();
        persist();
        conflictStreak += 1;
        if (conflictStreak <= 3) void flush();
        else {
            conflictStreak = 0;
            scheduleRetry();
        }
        return;
    }
    if (res.status === 400 || res.status === 413) {
        // The server refused the values (a client bug) — drop them and resync.
        console.warn("[preferences] server rejected a preferences change", res.status);
        serverKnown = false;
        void pull();
        return;
    }
    pending = mergePatches(sent, pending);
    persist();
    if (res.status === 401 || res.status === 403) {
        emit({ sync: "local" }); // retried when the token changes (usePreferencesSync)
        return;
    }
    scheduleRetry(); // 5xx / gateway: keep it locally and try again
}

/**
 * Starts syncing for the signed-in user. Switches the store to their mirror (migrating the
 * old keys the first time), fetches the account copy and saves anything pending.
 */
export function startPreferenceSync(email: string, tokenGetter: () => string | undefined): () => void {
    const user = email.trim().toLowerCase();
    getToken = tokenGetter;
    if (snapshot.user !== user) loadUser(user);
    persist(); // remember who is signed in, for the next boot
    void pull();

    const onOnline = () => void (serverKnown ? flush() : pull());
    const onVisible = () => {
        if (document.visibilityState !== "visible") return;
        if (Date.now() - pulledAt > 60_000 && !pending && !inflight) void pull();
    };
    // Leaving the page: send what's pending now (the PUT uses keepalive).
    const onPageHide = () => {
        if (pending && !inflight && serverKnown) void flush();
    };
    // Another tab of the same user changed something: show it here too.
    const onStorage = (e: StorageEvent) => {
        if (e.key !== mirrorKey(user) || inflight || snapshot.user !== user) return;
        const mirror = readMirror(user);
        if (!mirror) return;
        ({ base, version, pending, migration } = mirror);
        recompute();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("storage", onStorage);
    return () => {
        window.removeEventListener("online", onOnline);
        document.removeEventListener("visibilitychange", onVisible);
        window.removeEventListener("pagehide", onPageHide);
        window.removeEventListener("storage", onStorage);
        if (retryTimer) clearTimeout(retryTimer);
        retryTimer = undefined;
    };
}

/** A fresh token after a 401 / sign-in: try again. */
function kickPreferenceSync() {
    if (!snapshot.user) return;
    void (serverKnown ? flush() : pull());
}

/** Mount once inside the signed-in shell. */
export function usePreferencesSync() {
    const auth = useAuth();
    const email = (auth.user?.profile.email ?? "").toLowerCase();
    const token = auth.user?.access_token;
    const tokenRef = useRef(token);
    tokenRef.current = token;

    useEffect(() => {
        if (!email) return;
        return startPreferenceSync(email, () => tokenRef.current);
    }, [email]);

    useEffect(() => {
        if (token) kickPreferenceSync();
    }, [token]);

    // The suite look's account copy lives in the Drive BFF (shared by every app).
    useAppearanceSync(import.meta.env.VITE_DRIVE_BFF_BASE_URL, token, mailSavedLook);
}
